import React, { useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    TextField,
    Button,
    Grid,
    Stack,
    Divider,
    Alert,
    Snackbar,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import { contactService } from '../services/contactService';
import useSEO from '../hooks/useSEO';

const ContactPage: React.FC = () => {
    useSEO({
        title: 'تماس با ما',
        description: 'برای سوالات، پیشنهادات یا همکاری با تیم چیستا تماس بگیرید.',
        canonical: '/contact',
    });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);

        try {
            const result = await contactService.sendContactMessage(formData);
            if (result.success) {
                setStatus({ type: 'success', message: 'پیام شما با موفقیت دریافت شد. به‌زودی با شما تماس می‌گیریم.' });
                setFormData({ name: '', email: '', phone: '', message: '' });
            } else {
                setStatus({ type: 'error', message: 'متأسفانه در ارسال پیام خطایی رخ داد. لطفاً دوباره تلاش کنید.' });
            }
        } catch (error) {
            console.error('Submit error:', error);
            setStatus({ type: 'error', message: 'متأسفانه در ارسال پیام خطایی رخ داد. لطفاً دوباره تلاش کنید.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: { xs: 6, md: 10 } }}>
            {/* Hero Section */}
            <Box
                sx={{
                    width: '100%',
                    height: { xs: 150, md: 225 },
                    position: 'relative',
                    overflow: 'hidden',
                    mb: -4
                }}
            >
                <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1549923746-c502d488b3ea?auto=format&fit=crop&w=1200&q=80"
                    alt="Contact Cheesta Support Team"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            color: 'white',
                            fontSize: { xs: '2rem', md: '2.5rem' },
                            textShadow: '0 4px 15px rgba(0,0,0,0.6)',
                            textAlign: 'center',
                            px: 2
                        }}
                    >
                        تماس با ما
                    </Typography>
                </Box>
            </Box>

            <Container maxWidth="lg">
                <Paper elevation={4} sx={{ borderRadius: 6, overflow: 'hidden', position: 'relative', zIndex: 2, p: { xs: 3, md: 8 } }}>
                    <Grid container spacing={6}>
                        {/* Contact Form */}
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Box sx={{ direction: 'rtl', textAlign: 'right' }}>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a202c', mb: 2 }}>
                                    ارسال پیام
                                </Typography>
                                <Typography sx={{ color: '#718096', mb: 2, fontSize: '0.95rem', lineHeight: 1.8 }}>
                                    اگر پیشنهادی دارید یا هنگام ثبت‌نام با مشکلی مواجه شدید، از طریق فرم زیر با ما در ارتباط باشید. ما تلاش می‌کنیم در کوتاه‌ترین زمان ممکن به پیام شما پاسخ دهیم.
                                </Typography>
                                <Typography sx={{ color: '#e53e3e', mb: 4, fontWeight: 500, fontSize: '0.9rem', lineHeight: 1.8 }}>
                                    لطفا برای تماس با کسب و کار پست شده در وبسایت از طریق ایمیل آدرس و یا شماره تماس با آگهی دهنده در تماس باشید این فرم برای ارتباط با سایت چیستا میباشد.
                                </Typography>

                                {status && (
                                    <Alert severity={status.type} sx={{ mb: 4, direction: 'ltr' }}>
                                        {status.message}
                                    </Alert>
                                )}

                                <Box component="form" onSubmit={handleSubmit} sx={{ direction: 'ltr', textAlign: 'left' }}>
                                    <Stack spacing={3}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    label="نام"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    variant="outlined"
                                                    required
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    fullWidth
                                                    label="ایمیل"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    variant="outlined"
                                                    type="email"
                                                    required
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 12 }}>
                                                <TextField
                                                    fullWidth
                                                    label="شماره تماس"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    variant="outlined"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                />
                                            </Grid>
                                        </Grid>

                                        <TextField
                                            fullWidth
                                            label="پیام"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            variant="outlined"
                                            multiline
                                            rows={5}
                                            required
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                        />
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={loading}
                                            sx={{
                                                bgcolor: '#2854a1',
                                                py: 1.5,
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                '&:hover': { bgcolor: '#1a3a7a' }
                                            }}
                                        >
                                            {loading ? 'Sending...' : 'ارسال پیام'}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Contact info side */}
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Box sx={{ direction: 'rtl', textAlign: 'right', bgcolor: 'rgba(40, 84, 161, 0.05)', p: 4, borderRadius: 4, height: '100%' }}>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2854a1', mb: 4 }}>
                                    اطلاعات تماس
                                </Typography>

                                <Stack spacing={4}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 2, color: '#2854a1', display: 'flex' }}>
                                            <EmailIcon />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>پست الکترونیک</Typography>
                                            <Typography sx={{ color: '#4a5568' }}>support@cheesta.com.au</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 2, color: '#2854a1', display: 'flex' }}>
                                            <LocationOnIcon />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>موقعیت</Typography>
                                            <Typography sx={{ color: '#4a5568' }}>Sydney, Australia</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{ p: 1, bgcolor: 'white', borderRadius: 2, color: '#e1306c', display: 'flex' }}>
                                            <InstagramIcon />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>شبکه‌های اجتماعی</Typography>
                                            <Typography
                                                component="a"
                                                href="https://www.instagram.com/cheesta_australia/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: '#2854a1',
                                                    textDecoration: 'none',
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                            >
                                                ما را در اینستاگرام دنبال کنید
                                            </Typography>
                                        </Box>
                                    </Box>


                                </Stack>

                                <Divider sx={{ my: 4 }} />

                                <Typography variant="body2" sx={{ color: '#718096', lineHeight: 1.8 }}>
                                    چیستا مرجع کسب و کارهای ایرانیان مقیم استرالیا تلاش می‌کند تا هموطنان عزیز همدیگر را سریع تر و راحت تر بیایند و این جامعه با موفقیت در استرالیا رشد و پیشرفت نماید.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>

            <Snackbar
                open={status !== null}
                autoHideDuration={6000}
                onClose={() => setStatus(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {status ? (
                    <Alert onClose={() => setStatus(null)} severity={status.type} sx={{ width: '100%', direction: 'rtl' }}>
                        {status.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </Box>
    );
};

export default ContactPage;
